import React, { useContext } from "react";
import {
  Menu,
  Item,
  Separator,
  Submenu,
  useContextMenu,
} from "react-contexify";
import { GrigoraEditorContext } from "../context";
import { getRandomId as getRandomNodeId, ROOT_NODE } from "@craftjs/utils";

import "react-contexify/dist/ReactContexify.css";
import "./contextMenu.scss";

import { isMac } from "../../utils";
import Toast from "../../toast/toast";
import { CorrectIcon } from "../../../assets/ui-icons";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { toast } from "react-toastify";
import { addNodeTree, handleSaveComponent } from "../component-blocks";
import { useEditor, useNode } from "@craftjs/core";
import { emitOnCopy, waitForElement, waitForLastElement } from "../../dom";
import { blocksLibrary } from "../../blocks";
import blockActions from "../../block-actions";
import { getTempStateLocalStorage } from "../../local-store";

function MenuContent({ title, shortcut }) {
  return (
    <div className="flex w-full justify-between items-center">
      <span>{title}</span>
      <span>
        {shortcut && (
          <span className="text-xs text-gray-500">
            {isMac() ? shortcut.mac : shortcut.windows}
          </span>
        )}
      </span>
    </div>
  );
}

function ContextMenu() {
  const { editorDocument } = useContext(GrigoraDashboardContext);

  const { CONTEXT_MENU_ID, hideContextMenu, setIsBlockSettingsVisible } =
    useContext(GrigoraEditorContext);

  const { query, actions, id, slug, isCanvas, childrens, parents } = useEditor(
    (_, query) => {
      const id = query.getEvent("selected").first();

      return {
        id: id,
        slug: id ? query.node(id)?.get()?.data?.custom?.slug : "",
        isCanvas: id ? query.node(id)?.isCanvas() : false,
        childrens: id ? query.node(id)?.childNodes() : [],
        parents: id
          ? query
              .node(id)
              ?.ancestors(10)
              ?.filter((node) => node !== ROOT_NODE)
          : [],
      };
    },
  );

  function onSettings() {
    setIsBlockSettingsVisible(true);
  }

  function onCut() {
    if (!id) return; // If no node is selected, return

    emitOnCopy(editorDocument.querySelector(".grigora-editor-renderer-canvas"));
    actions.delete(id);
    toast.success(<Toast title={"Moved to Clipboard"} />, {
      icon: <CorrectIcon />,
    });
  }

  function onCopy() {
    if (!id) return; // If no node is selected, return

    emitOnCopy(editorDocument.querySelector(".grigora-editor-renderer-canvas"));
    toast.success(<Toast title={"Copied"} />, {
      icon: <CorrectIcon />,
    });
  }

  const onDuplicate = () => {
    blockActions.duplicate(actions, query, id);
  };

  function onDelete() {
    blockActions.delete(actions, query, id);
  }

  const insertBlock = (type, before = false) => {
    blockActions.insert(actions, query, id, type, before);
  };

  const insertBlockInside = (type, before = false) => {
    blockActions.insertInside(actions, query, id, type, before);
    hideContextMenu();
  };

  function onWrapContainer() {
    const parentNodeId = query?.node(id)?.get()?.data?.parent;
    const selectedNodeSlug = query.node(parentNodeId)?.get()?.data
      ?.custom?.slug;

    if (selectedNodeSlug === "grigora/slider") {
      return;
    }

    blockActions.wrap(actions, query, "grigora/container", editorDocument);
  }

  function onUnwrapContainer() {
    blockActions.unwrap(actions, query, id, editorDocument);
  }

  function onPaste() {
    blockActions.paste(
      actions,
      query,
      id,
      { grigoraBlockJson: getTempStateLocalStorage("copiedBlocks") },
      editorDocument,
    );
  }

  // I'm using a single event handler for all items
  // but you don't have too :)
  const handleItemClick = ({ id, event, props }) => {
    switch (id) {
      case "settings":
        onSettings();
        break;
      case "cut":
        onCut();
        break;
      case "copy":
        onCopy();
        break;
      case "duplicate":
        onDuplicate();
        break;
      case "delete":
        onDelete();
        break;
      case "insert-before":
        insertBlock("grigora/text", true);
        break;
      case "insert-after":
        insertBlock("grigora/text");
        break;
      case "insert-inside":
        insertBlockInside("grigora/text");
        break;
      case "wrap-container":
        onWrapContainer();
        break;
      case "unwrap-container":
        onUnwrapContainer();
        hideContextMenu();
        break;
      case "paste":
        onPaste();
        hideContextMenu();
        break;
      //etc...
    }
  };

  return (
    <Menu id={CONTEXT_MENU_ID}>
      <Item id={"settings"} onClick={handleItemClick}>
        <MenuContent
          title={"Settings"}
          shortcut={
            {
              // windows: "Ctrl + `",
              // mac: "⌘`",
            }
          }
        />
      </Item>
      <Separator />
      {parents && parents.length > 0 ? (
        <>
          <Submenu label="Select Parent">
            {parents.map((parent) => {
              var node = query.node(parent);
              var nodeData = node?.get();
              var name =
                nodeData?.data?.custom?.displayName ||
                nodeData?.data?.displayName;
              if (parent === ROOT_NODE) return null;
              return (
                <Item
                  key={`select-parent-${parent}`}
                  onMouseOver={(e) => {
                    actions.hoverNode(parent);
                  }}
                  onClick={() => {
                    actions.selectNode(parent);
                    hideContextMenu();
                  }}
                  id="reload"
                >
                  {name}
                </Item>
              );
            })}
          </Submenu>
          <Separator />
        </>
      ) : (
        <></>
      )}
      <Item id={"wrap-container"} onClick={handleItemClick}>
        <MenuContent
          title={"Wrap in Container"}
          shortcut={
            {
              // windows: "Ctrl + g",
              // mac: "⌘G",
            }
          }
        />
      </Item>
      {slug === "grigora/container" && childrens.length > 0 && (
        <>
          <Item id={"unwrap-container"} onClick={handleItemClick}>
            <MenuContent
              title={"Unwrap"}
              shortcut={
                {
                  // windows: "Ctrl + g",
                  // mac: "⌘G",
                }
              }
            />
          </Item>
        </>
      )}
      <Separator />
      <Item id={"cut"} onClick={handleItemClick}>
        <MenuContent
          title={"Cut"}
          shortcut={
            {
              // windows: "Ctrl + x",
              // mac: "⌘X",
            }
          }
        />
      </Item>
      <Item id={"copy"} onClick={handleItemClick}>
        <MenuContent
          title={"Copy"}
          shortcut={
            {
              // windows: "Ctrl + c",
              // mac: "⌘C",
            }
          }
        />
      </Item>
      <Item id={"duplicate"} onClick={handleItemClick}>
        <MenuContent
          title={"Duplicate"}
          shortcut={
            {
              // windows: "Ctrl + d",
              // mac: "⌘D",
            }
          }
        />
      </Item>
      <Item
        id={"paste"}
        onClick={handleItemClick}
        disabled={!getTempStateLocalStorage("copiedBlocks")}
      >
        <MenuContent
          title={"Paste"}
          shortcut={
            {
              // windows: "Ctrl + d",
              // mac: "⌘D",
            }
          }
        />
      </Item>
      <Separator />
      <Item id={"insert-before"} onClick={handleItemClick}>
        <MenuContent
          title={"Insert Before"}
          shortcut={
            {
              // windows: "Ctrl + c",
              // mac: "⌘C",
            }
          }
        />
      </Item>
      <Item id={"insert-after"} onClick={handleItemClick}>
        <MenuContent
          title={"Insert After"}
          shortcut={
            {
              // windows: "Ctrl + c",
              // mac: "⌘C",
            }
          }
        />
      </Item>
      {isCanvas ? (
        <>
          <Item id={"insert-inside"} onClick={handleItemClick}>
            <MenuContent
              title={"Insert Inside"}
              shortcut={
                {
                  // windows: "Ctrl + c",
                  // mac: "⌘C",
                }
              }
            />
          </Item>
        </>
      ) : (
        <></>
      )}
      <Separator />
      <Item id={"delete"} onClick={handleItemClick}>
        Delete
      </Item>
    </Menu>
  );
}

export default ContextMenu;
